// App.js
import React, { useState, useEffect } from "react";
import Auth from "./components/Auth/Auth";
import Scanner from "./components/Scanner/Scanner";
import ScannedDataContainer from "./components/ScannedData/ScannedDataContainer";
import AdminUserSelector from "./components/AdminUserSelector/AdminUserSelector";
import UpdateNotification from './components/UpdateNotification/UpdateNotification';
import { auth, database } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { ref, get } from "firebase/database";
import versionData from "./version.json";
import VersionModal from "./components/VersionModal/VersionModal";
import AddressEditor from "./components/AddressEditor/AddressEditor";
import PhotoCapture from "./components/PhotoCapture/PhotoCapture";
import "./index.css";

function App() {
  const [user, setUser] = useState(null);
  const [view, setView] = useState("auth");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);

  const [uniqueKey, setUniqueKey] = useState(null);
  const [userID, setUserID] = useState(null);

  // NEW: State to track if we are editing an existing item from the list
  const [isEditingFromList, setIsEditingFromList] = useState(false);

  // Admin-related state
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedViewUserID, setSelectedViewUserID] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        // Check if user is admin on reload
        let adminStatus = false;
        try {
          const sanitizedEmail = firebaseUser.email.replace(/\./g, "-");
          const adminRef = ref(database, `data/settings/admins/${sanitizedEmail}`);
          const snapshot = await get(adminRef);
          adminStatus = snapshot.exists() && snapshot.val() === true;
        } catch (err) {
          console.error("Failed to check admin status", err);
        }

        setIsAdmin(adminStatus);
        // Set appropriate view based on admin status
        if (adminStatus) {
          setView("adminUserSelector");
        } else {
          setView("scannedData");
        }
      }
      setCheckingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSignIn = (loggedInUser, adminStatus = false) => {
    setUser(loggedInUser);
    setIsAdmin(adminStatus);
    if (adminStatus) {
      setView("adminUserSelector");
    } else {
      setView("scannedData");
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    window.location.reload();
  };

  const handleUserSelected = (userId) => {
    setSelectedViewUserID(userId);
    setView("scannedData");
  };

  const handleBackToUserSelection = () => {
    setView("adminUserSelector");
    setSelectedViewUserID(null);
  };

  // Handlers for redundant scan navigation
  const handleRedundantConfirmAddress = (userPath, scanKey) => {
    setUserID(userPath);
    setUniqueKey(scanKey);
    setIsEditingFromList(true); // Existing scan
    setView("reviewAddress");
  };

  const handleRedundantTakePhoto = (userPath, scanKey) => {
    setUserID(userPath);
    setUniqueKey(scanKey);
    setView("photoCapture");
  };

  if (checkingAuth) return <div>Loading...</div>;

  return (
    <div className="App app-container">
      <UpdateNotification />
      <h1>Delivery Tracker{isAdmin ? " Admin" : ""}</h1>

      {view === "auth" && <Auth onSignIn={handleSignIn} />}

      {view === "adminUserSelector" && (
        <AdminUserSelector
          onUserSelected={handleUserSelected}
          onSignOut={handleSignOut}
        />
      )}

      {view === "scannedData" && (
                <ScannedDataContainer
          user={user}
          viewUserID={selectedViewUserID}
          isAdmin={isAdmin}
          onStartScan={() => {
            // New scan flow
            setIsEditingFromList(false);
            setView("scanner");
          }}
          onOpenPhotoCapture={(userPath, scanKey) => {
            // userPath is the normalized email (dots replaced with hyphens)
            setUserID(userPath);
            setUniqueKey(scanKey);
            setView("photoCapture");
          }}
          // Handler to open Address Editor from the table
          onOpenAddressEditor={(userPath, scanKey) => {
            setUserID(userPath);
            setUniqueKey(scanKey);
            // Mark that we came from the list
            setIsEditingFromList(true);
            setView("reviewAddress");
          }}
          onBackToUserSelection={handleBackToUserSelection}
        />
      )}

      {view === "scanner" && (
        <Scanner
          user={user}
          setUniqueKey={setUniqueKey}
          setUserID={setUserID}
          onStopScan={() => {
            // Flow: Scan -> Address -> Photo
            setIsEditingFromList(false);
            setView("reviewAddress");
          }}
          onCancel={() => {
            setView("scannedData");
          }}
          onConfirmAddress={handleRedundantConfirmAddress}
          onTakePhoto={handleRedundantTakePhoto}
        />
      )}

      {view === "reviewAddress" && uniqueKey && userID && (
        <AddressEditor
          userID={userID}
          uniqueKey={uniqueKey}
          onDone={(hasImage) => {
            // LOGIC CHANGE: Check flow + DB Image existence
            if (hasImage === null) {
              // User clicked "Back to Dashboard" in AddressEditor
              setUniqueKey(null);
              setUserID(null);
              setIsEditingFromList(false);
              setView("scannedData");
              return;
            }

            if (isEditingFromList) {
              // We came from the Dashboard list.
              // If image is missing (!hasImage), go to PhotoCapture.
              // If image exists, go back to Dashboard.
              if (!hasImage) {
                setView("photoCapture");
              } else {
                setUniqueKey(null);
                setUserID(null);
                setIsEditingFromList(false);
                setView("scannedData");
              }
            } else {
              // If we came from Scanner (New Scan), always proceed to Photo Capture
              setView("photoCapture");
            }
          }}
        />
      )}

      {view === "photoCapture" && uniqueKey && userID && (
        <PhotoCapture
          userID={userID}
          uniqueKey={uniqueKey}
          onPhotoDone={() => {
            // Reset record and return to scanned data
            setUniqueKey(null);
            setUserID(null);
            setView("scannedData");
          }}
        />
      )}

      {versionData && (
        <footer
          className="version-footer"
          onClick={() => {
            if (isAdmin) setIsVersionModalOpen(true);
          }}
        >
          Version: {versionData.version || "N/A"}
          {isAdmin && " (click for more)"}
        </footer>
      )}

      {isAdmin && isVersionModalOpen && (
        <VersionModal
          versionData={versionData}
          onClose={() => setIsVersionModalOpen(false)}
        />
      )}
    </div>
  );
}

export default App;